/**
 * Adresář / Kontakty — pole z `users.profile` JSONB (+ HR id → label)
 * a sloupců HR (`users.phone`, `hr_company_id` → companyName, `region` = pobočka).
 * Parita auth-service `GET users/activity` / `directory` a Android Kontakty.
 */

import {
  HR_CITY_LABELS,
  HR_COMPANY_BY_ID,
  isHrCityId,
  isHrCompanyId,
  type HrCityId,
  type HrCompanyId,
} from '../constants/hr-onboarding.constants';
import { POLYGON_RESPONSIBLE_BRANCHES } from '../constants/polygon-business.constants';

export type UserDirectoryOrgFields = {
  phone: string | null;
  /** Obchodní jméno firmy (např. „ProTelco s.r.o.“). */
  company: string | null;
  /** Zodpovědná pobočka — kanonicky `POLYGON_RESPONSIBLE_BRANCHES`. */
  branch: string | null;
};

function readTrimmedString(
  profile: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const raw = profile[key];
    if (typeof raw !== 'string') continue;
    const value = raw.trim();
    if (value.length > 0) return value;
  }
  return null;
}

function normalizeBranchLabel(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const exact = POLYGON_RESPONSIBLE_BRANCHES.find(
    (b) => b.toLowerCase() === trimmed.toLowerCase(),
  );
  return exact ?? trimmed;
}

/** companyId + cityId → kanonická pobočka (parita polygon `responsible_branch`). */
export function branchFromHrIds(
  companyId: string | null | undefined,
  cityId: string | null | undefined,
): string | null {
  if (!isHrCompanyId(companyId)) return null;
  if (companyId === 'fiber_beta') return 'Fiber Beta';
  if (companyId === 'extermis') return 'EXTERMIS';
  if (companyId === 'protelco' && isHrCityId(cityId)) {
    const city = HR_CITY_LABELS[cityId as HrCityId];
    const candidate = `ProTelco ${city}`;
    return POLYGON_RESPONSIBLE_BRANCHES.includes(
      candidate as (typeof POLYGON_RESPONSIBLE_BRANCHES)[number],
    )
      ? candidate
      : null;
  }
  return null;
}

export function companyLabelFromHrId(companyId: string | null | undefined): string | null {
  if (!isHrCompanyId(companyId)) return null;
  return HR_COMPANY_BY_ID[companyId as HrCompanyId]?.name ?? null;
}

/** Odvození firmy z kanonické pobočky, když profile nemá company. */
export function companyFromBranch(branch: string | null | undefined): string | null {
  const b = (branch ?? '').trim();
  if (!b) return null;
  const lower = b.toLowerCase();
  if (lower.startsWith('protelco')) return HR_COMPANY_BY_ID.protelco.name;
  if (lower === 'fiber beta' || lower.startsWith('fiber')) return HR_COMPANY_BY_ID.fiber_beta.name;
  if (lower === 'extermis' || lower.startsWith('extermis')) return HR_COMPANY_BY_ID.extermis.name;
  return null;
}

/** Telefon z `users.profile.phone` (kontakty / RBAC). */
export function extractProfilePhone(profile: unknown): string | null {
  if (!profile || typeof profile !== 'object') return null;
  return readTrimmedString(profile as Record<string, unknown>, ['phone']);
}

/**
 * Firma + pobočka z profile (volitelné klíče) nebo HR `companyId`/`cityId`.
 * Telefon stejný helper jako dřív.
 */
export function extractUserDirectoryOrgFields(profile: unknown): UserDirectoryOrgFields {
  if (!profile || typeof profile !== 'object') {
    return { phone: null, company: null, branch: null };
  }
  const p = profile as Record<string, unknown>;
  const phone = readTrimmedString(p, ['phone']);

  const companyIdRaw = readTrimmedString(p, ['companyId', 'company_id']);
  const cityIdRaw = readTrimmedString(p, ['cityId', 'city_id']);

  const branchDirect = readTrimmedString(p, [
    'branch',
    'pobocka',
    'pobočka',
    'responsibleBranch',
    'responsible_branch',
  ]);
  let branch =
    (branchDirect ? normalizeBranchLabel(branchDirect) : null)
    ?? branchFromHrIds(companyIdRaw, cityIdRaw);

  let company =
    readTrimmedString(p, [
      'company',
      'firma',
      'companyName',
      'company_name',
      'responsibleCompanyName',
      'responsible_company_name',
    ]) ?? companyLabelFromHrId(companyIdRaw) ?? companyFromBranch(branch);

  if (!branch && cityIdRaw && isHrCityId(cityIdRaw)) {
    // Samotné město (bez companyId) — zobraz aspoň label města jako pobočku.
    branch = HR_CITY_LABELS[cityIdRaw as HrCityId] ?? cityIdRaw;
  }

  return {
    phone,
    company: company?.trim() || null,
    branch: branch?.trim() || null,
  };
}

/**
 * Top-level phone/company/branch pro activity / by-role (Android Kontakty 81):
 * HR sloupce mají přednost před legacy `profile` JSONB (seed píše jen sloupce).
 * `region` = pobočka; když chybí, fallback na `kraj` (CSV seed teď plní hlavně kraj).
 */
export function resolveUserDirectoryOrgFields(
  profile: unknown,
  hr?: {
    phone?: string | null;
    companyName?: string | null;
    region?: string | null;
    kraj?: string | null;
  } | null,
): UserDirectoryOrgFields {
  const org = extractUserDirectoryOrgFields(profile);
  const hrPhone = typeof hr?.phone === 'string' ? hr.phone.trim() : '';
  const hrCompany = typeof hr?.companyName === 'string' ? hr.companyName.trim() : '';
  const hrRegion = typeof hr?.region === 'string' ? hr.region.trim() : '';
  const hrKraj = typeof hr?.kraj === 'string' ? hr.kraj.trim() : '';
  return {
    phone: hrPhone || org.phone,
    company: hrCompany || org.company,
    branch: org.branch || hrRegion || hrKraj || null,
  };
}
